/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
});

// Milliseconds to token expiry, or 0 if unreadable.
function msToExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 - Date.now() : 0;
  } catch (e) {
    return 0;
  }
}

// DISPATCH-SPEED LAW: a dispatcher must never hit a login screen mid-shift.
// Tokens are 30 days; we proactively refresh when within 7 days of expiry, so
// an active session renews forever. Refresh is fire-and-forget on the request
// path so it never blocks a dispatch action.
let refreshing = false;
const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('nemt_token');
  if (token) {
    const left = msToExpiry(token);
    if (left > 0 && left < SEVEN_DAYS && !refreshing) {
      refreshing = true;
      axios.post('/api/auth/refresh', {}, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => { if (res.data && res.data.token) localStorage.setItem('nemt_token', res.data.token); })
        .catch(() => { /* keep using the current token until it truly expires */ })
        .finally(() => { refreshing = false; });
    }
    config.headers.Authorization = `Bearer ${localStorage.getItem('nemt_token')}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Remember where they were so login can return them to it (e.g. a
      // bookmarked /rooms?room=lgh) instead of dumping them on Today.
      const here = window.location.pathname + window.location.search;
      if (here && here !== '/login') localStorage.setItem('nemt_return_to', here);
      localStorage.removeItem('nemt_token');
      localStorage.removeItem('nemt_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
